import { useState, useEffect } from "react";
import { PlayCircle, ArrowLeft, X } from "lucide-react";
import api from "@/lib/api";

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  let videoId = null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      videoId = u.searchParams.get("v");
    } else if (u.hostname === "youtu.be") {
      videoId = u.pathname.slice(1);
    } else if (u.hostname.includes("youtube.com") && u.pathname.includes("/embed/")) {
      videoId = u.pathname.split("/embed/")[1];
    }
  } catch {
    const match = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) videoId = match[1];
  }
  return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : null;
}

export default function FreeClassesPage() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    api.get("/free-classes")
      .then(({ data }) => setClasses(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="free-classes-loading">
        <div className="animate-pulse text-slate-400 text-sm font-medium">Loading free classes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="free-classes-page">
      {/* Header */}
      <div className="bg-[#0284C7] px-5 pt-6 pb-5">
        <div className="flex items-center gap-3 mb-1">
          <PlayCircle size={24} className="text-white" />
          <h1 className="text-lg font-black text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Free Classes
          </h1>
        </div>
        <p className="text-xs text-white/70 ml-9">Watch free video lectures for exam preparation</p>
      </div>

      {/* Video Player Modal */}
      {activeVideo && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex flex-col" data-testid="video-player-modal">
          <div className="flex items-center justify-between px-4 py-3 bg-black/50">
            <h2 className="text-sm font-bold text-white truncate flex-1">{activeVideo.title}</h2>
            <button data-testid="close-video-btn" onClick={() => setActiveVideo(null)} className="text-white ml-3">
              <X size={22} />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            {getYouTubeEmbedUrl(activeVideo.youtube_url) ? (
              <iframe
                data-testid="youtube-player"
                src={getYouTubeEmbedUrl(activeVideo.youtube_url)}
                className="w-full max-w-3xl aspect-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={activeVideo.title}
              />
            ) : (
              <div className="text-white text-sm text-center p-6">
                <p className="mb-3">Unable to embed video</p>
                <a href={activeVideo.youtube_url} target="_blank" rel="noopener noreferrer" className="text-[#0284C7] underline font-bold">
                  Open in YouTube
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 pt-4 space-y-3">
        {classes.length === 0 ? (
          <div className="text-center py-16" data-testid="free-classes-empty">
            <PlayCircle size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-500">No free classes available yet</p>
            <p className="text-xs text-slate-400 mt-1">Check back soon for new content</p>
          </div>
        ) : (
          classes.map((cls) => {
            const embedUrl = getYouTubeEmbedUrl(cls.youtube_url);
            const thumbUrl = cls.thumbnail || (embedUrl ? `https://img.youtube.com/vi/${embedUrl.split("/embed/")[1]?.split("?")[0]}/hqdefault.jpg` : null);
            return (
              <button
                key={cls.id}
                data-testid={`free-class-${cls.id}`}
                onClick={() => setActiveVideo(cls)}
                className="w-full bg-white border border-slate-100 overflow-hidden text-left hover:border-[#0284C7]/30 transition-none"
              >
                {/* Thumbnail */}
                <div className="aspect-video w-full bg-slate-200 relative">
                  {thumbUrl ? (
                    <img src={thumbUrl} alt={cls.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                      <PlayCircle size={40} className="text-slate-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-14 h-14 rounded-full bg-[#0284C7] flex items-center justify-center shadow-lg">
                      <PlayCircle size={28} className="text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-bold text-slate-900">{cls.title}</h3>
                  {cls.description && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{cls.description}</p>
                  )}
                  <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 inline-block mt-2">FREE</span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
